import React from "react";
import { ArrowLeft, MapPin, Crown, Users, Video, Mic, Heart, Sparkles } from "lucide-react";
import { ExhibitionBooth, ExhibitionCountry, ExhibitionCity } from "@/hooks/useExhibition";
import { LiveBadge } from "@/components/live-arenas/LiveBadge";
import BackgroundBalloons from "@/components/pop-balloon/BackgroundBalloons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ExhibitionBoothPreviewProps {
  booth: ExhibitionBooth;
  city?: ExhibitionCity;
  country?: ExhibitionCountry;
  currentUserId?: string;
  onBack: () => void;
  onEnterBooth: () => void;
}

const INTENT_COLORS: Record<string, string> = {
  flirting: "from-pink-500 to-rose-500",
  serious: "from-purple-500 to-violet-500",
  elite: "from-amber-500 to-yellow-500",
  faith: "from-blue-500 to-cyan-500",
  fun: "from-green-500 to-emerald-500",
  casual: "from-orange-500 to-amber-500",
};

const INTENT_LABELS: Record<string, string> = {
  flirting: "Flirting",
  serious: "Serious",
  elite: "Elite",
  faith: "Faith",
  fun: "Fun",
  casual: "Casual",
};

const ExhibitionBoothPreview: React.FC<ExhibitionBoothPreviewProps> = ({
  booth,
  city,
  country,
  currentUserId,
  onBack,
  onEnterBooth,
}) => {
  const intent = booth.exhibition_intent || "casual";
  const isHost = currentUserId === booth.created_by;
  const hasSpace = booth.participants_count < booth.max_participants;
  const isLive = booth.participants_count > 0;

  return (
    <div className="min-h-screen sky-gradient relative overflow-hidden">
      <BackgroundBalloons />

      <div className="relative z-10 flex flex-col min-h-screen pb-20">
        {/* Header */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <button onClick={onBack} className="p-2 -ml-2">
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            {isLive && <LiveBadge variant="glow" />}
          </div>
        </div>

        {/* Booth Header Card */}
        <div className="mx-4 mt-2 p-6 rounded-3xl backdrop-blur-xl bg-white/60 border border-white/70 shadow-xl">
          {/* Location */}
          <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
            <MapPin className="w-4 h-4" />
            <span>{city?.name}</span>
            <span>•</span>
            <span>{country?.flag} {country?.name}</span>
          </div>

          {/* Intent badge */}
          <div
            className={`inline-flex px-3 py-1 rounded-full text-white text-sm font-semibold bg-gradient-to-r ${INTENT_COLORS[intent]} mb-3`}
          >
            {INTENT_LABELS[intent]}
          </div>

          {/* Booth name */}
          <h1 className="text-2xl font-bold text-foreground mb-2">{booth.name}</h1>

          {/* Host */}
          <div className="flex items-center gap-3 mb-4">
            {booth.host_photo && (
              <div className="relative">
                <img
                  src={booth.host_photo}
                  alt={booth.host_name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white"
                />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
                  <Crown className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-foreground">
                {booth.host_name}
                {isHost && <span className="text-amber-600 ml-1">(You)</span>}
              </p>
              <p className="text-xs text-muted-foreground">Host</p>
            </div>
          </div>

          {/* Description */}
          {booth.description && (
            <p className="text-muted-foreground text-sm mb-4">{booth.description}</p>
          )}

          {/* Stats row */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="font-semibold">{booth.participants_count}</span>
              <span className="text-muted-foreground">/ {booth.max_participants}</span>
            </div>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
              {booth.is_video_enabled ? <Video className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
              {booth.is_video_enabled ? "Video" : "Voice"}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase">
              {booth.language}
            </span>
          </div>
        </div>

        {/* Live Participants Preview */}
        {(booth.featured_participants?.length ?? 0) > 0 && (
          <div className="mx-4 mt-6">
            <h2 className="text-lg font-bold text-foreground mb-3">Who's Here</h2>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {booth.featured_participants?.map((p) => (
                <div
                  key={p.id}
                  className="flex-shrink-0 w-20 text-center"
                >
                  <div className="relative mx-auto w-16 h-16 rounded-full overflow-hidden mb-2 ring-2 ring-green-500 shadow-lg shadow-green-500/30">
                    <img
                      src={p.photo_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${p.id}`}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-xs font-medium text-foreground truncate">{p.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom CTA - Simple instant entry */}
        <div className="fixed bottom-20 left-0 right-0 px-6">
          <div className="max-w-sm mx-auto space-y-3">
            {isHost ? (
              <>
                <div className="flex items-center justify-center gap-2 text-amber-600">
                  <Crown className="w-5 h-5" />
                  <span className="font-semibold">You are the host</span>
                </div>
                <Button
                  onClick={onEnterBooth}
                  className="w-full py-6 text-lg font-bold rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-xl"
                >
                  <Video className="w-5 h-5 mr-2" />
                  Open Your Booth
                </Button>
              </>
            ) : hasSpace ? (
              <Button
                onClick={onEnterBooth}
                className="w-full py-6 text-lg font-bold rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-xl pulse-glow"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Enter Booth
              </Button>
            ) : (
              <Button
                disabled
                className="w-full py-6 text-lg font-bold rounded-full bg-muted text-muted-foreground"
              >
                Booth Full
              </Button>
            )}

            {!isHost && hasSpace && (
              <p className="text-center text-sm text-muted-foreground">
                Join instantly • No approval needed
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExhibitionBoothPreview;
